/**
 * useAgentProgressRefresh
 *
 * 统一管理 Agent Progress 页面的 WebSocket 推送 → 节流刷新管线。
 *
 * **重要**：项目当前 PushEventType 中并不存在 `BOTTLENECK_CHANGED` /
 * `INQUIRY_CONTACT_LIST_CHANGED` / `INQUIRY_TASK_FINISHED` 等事件。
 * 实际复用与原 contact-progress 完全一致的 4 个事件：
 *  - CONTACT_UNREAD_COUNT
 *  - SESSION_NEW_MSG
 *  - SESSION_TASK_RUNNING
 *  - SESSION_MESSAGE_CHANGED（仅匹配 inquiryId 时）
 *
 * 节流策略：10s leading + trailing；与原页面行为 1:1。
 *
 * SESSION_MESSAGE_CHANGED 命中（且 inquiryId 匹配）时直接触发 refreshInquiry，
 * 用于 computationStatus 实时更新。
 */

import { useThrottleFn } from 'ahooks';
import { usePushMessage, PushEventType } from '@/hooks/useWebSocket';

export interface UseAgentProgressRefreshOptions {
  inquiryId: string;
  sessionId?: string;
  /** detail 全量刷新 */
  refreshInquiry: () => Promise<void> | void;
  /** 公司列表刷新 */
  refreshContacts: () => Promise<void> | void;
  /** clarification 列表刷新 */
  refreshClarifications?: () => Promise<void> | void;
}

export function useAgentProgressRefresh(options: UseAgentProgressRefreshOptions): void {
  const { inquiryId, sessionId, refreshInquiry, refreshContacts, refreshClarifications } = options;

  const { run: throttledRefresh } = useThrottleFn(
    () => {
      void refreshInquiry();
      void refreshContacts();
    },
    { wait: 10000, leading: true, trailing: true }
  );

  usePushMessage(PushEventType.CONTACT_UNREAD_COUNT, () => {
    throttledRefresh();
  });

  usePushMessage<{ sessionId: string }>(PushEventType.SESSION_NEW_MSG, message => {
    if (sessionId && message.content?.sessionId !== sessionId) return;
    throttledRefresh();
  });

  usePushMessage<{ sessionId: string; inquiryTaskRunning: 0 | 1 }>(PushEventType.SESSION_TASK_RUNNING, message => {
    if (sessionId && message.content?.sessionId !== sessionId) return;
    throttledRefresh();
  });

  /**
   * SESSION_MESSAGE_CHANGED 仅匹配当前 inquiryId 时触发；
   * 直接触发 refreshInquiry（含 detail 重新拉取），与原页面行为对齐。
   */
  usePushMessage<{ sessionId: string; subMessageId: string; inquiryId: string }>(
    PushEventType.SESSION_MESSAGE_CHANGED,
    message => {
      if (message.content?.inquiryId !== inquiryId) return;
      void refreshInquiry();
    }
  );

  /**
   * INQUIRY_CONTACT_CHANGE 当 inquiryTaskId 匹配当前页面 inquiryId 时，
   * 刷新 clarification 列表与联系人列表。
   */
  usePushMessage<{ inquiryTaskId: string }>(PushEventType.INQUIRY_CONTACT_CHANGE, message => {
    if (message.content?.inquiryTaskId !== inquiryId) return;
    void refreshContacts();
    void refreshClarifications?.();
  });
}

export default useAgentProgressRefresh;
